import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import { apiErrorMessage, getCampaignLink } from "../../api/campaignLinks";
import { STANDARD_FIELDS, formatDateTime } from "./campaignLinkUrl";

const labelCell = { color: "text.secondary", width: 180, verticalAlign: "top", py: 0.75, pl: 0 };
const valueCell = { py: 0.75, wordBreak: "break-word" };

function LinkBox({ url, onCopy, label = "Copy link" }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Box
        sx={{
          flex: 1,
          p: 1,
          borderRadius: 1.5,
          backgroundColor: "#f5f5f5",
          fontFamily: "monospace",
          fontSize: "0.78rem",
          wordBreak: "break-all",
        }}
      >
        {url}
      </Box>
      <Tooltip title={label} arrow placement="top">
        <IconButton color="primary" size="small" aria-label={label} onClick={() => onCopy(url)}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** Read-only view of a campaign. Loads the campaign itself. */
function CampaignLinkDetailsDialog({ campaignId, onClose, onCopy, onEdit, onDuplicate }) {
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (campaignId == null) return;
    setCampaign(null);
    setError(null);
    setIsLoading(true);
    getCampaignLink(campaignId)
      .then(setCampaign)
      .catch((err) => setError(apiErrorMessage(err, "Failed to load the campaign.")))
      .finally(() => setIsLoading(false));
  }, [campaignId]);

  const isDeleted = campaign?.status === "DELETED";

  return (
    <Dialog open={campaignId != null} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {campaign ? campaign.utmCampaign : "Campaign details"}
        {campaign && (
          <Chip
            size="small"
            variant="outlined"
            label={isDeleted ? "Deleted" : "Active"}
            color={isDeleted ? "error" : "success"}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {campaign && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Generated link
            </Typography>
            <LinkBox url={campaign.generatedUrl} onCopy={onCopy} />

            <Table size="small" sx={{ mt: 2, "& td": { borderBottom: "none" } }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCell}>Destination</TableCell>
                  <TableCell sx={valueCell}>{campaign.destinationLabel || campaign.destination}</TableCell>
                </TableRow>
                {STANDARD_FIELDS.map((f) => (
                  <TableRow key={f.key}>
                    <TableCell sx={labelCell}>
                      {f.label} <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>({f.param})</Box>
                    </TableCell>
                    <TableCell sx={valueCell}>{campaign[f.key] || "—"}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={labelCell}>Custom parameters</TableCell>
                  <TableCell sx={valueCell}>
                    {(campaign.customParams || []).length === 0
                      ? "—"
                      : campaign.customParams.map((p) => (
                          <Chip key={p.name} size="small" label={`${p.name} = ${p.value}`} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={labelCell}>Notes</TableCell>
                  <TableCell sx={valueCell}>{campaign.notes || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={labelCell}>Created</TableCell>
                  <TableCell sx={valueCell}>
                    {formatDateTime(campaign.createdAt)} by {campaign.createdByName || "—"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={labelCell}>Last updated</TableCell>
                  <TableCell sx={valueCell}>
                    {formatDateTime(campaign.updatedAt)} by {campaign.updatedByName || "—"}
                  </TableCell>
                </TableRow>
                {isDeleted && (
                  <TableRow>
                    <TableCell sx={labelCell}>Deleted</TableCell>
                    <TableCell sx={valueCell}>
                      {formatDateTime(campaign.deletedAt)} by {campaign.deletedByName || "—"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
          Close
        </Button>
        {campaign && (
          <Button onClick={() => onDuplicate(campaign)} {...BUTTON_STYLES.secondary}>
            Duplicate
          </Button>
        )}
        {campaign && !isDeleted && (
          <Button onClick={() => onEdit(campaign)} {...BUTTON_STYLES.primary}>
            Edit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CampaignLinkDetailsDialog;
